const pendingOperations = new Set<Promise<unknown>>();

/**
 * Keeps an app-level operation visible to the update coordinator until the
 * promise returned to its caller settles. This is intentionally independent
 * of React so uploads started by any page can protect themselves from a
 * build-triggered reload.
 */
export function trackPendingOperation<T>(operation: Promise<T>): Promise<T> {
  pendingOperations.add(operation);
  const remove = () => pendingOperations.delete(operation);
  void operation.then(remove, remove);
  return operation;
}

/**
 * Waits for all operations that are currently in flight. New operations that
 * start while the drain is running are picked up by the next loop pass.
 * Settled failures do not block a reload; the operation's own caller remains
 * responsible for surfacing its error.
 */
export async function waitForPendingOperations(): Promise<void> {
  while (pendingOperations.size > 0) {
    await Promise.allSettled(Array.from(pendingOperations));
  }
}

/**
 * Snapshot of how many tracked operations are currently in flight. Exposed
 * for diagnostic logging (e.g. attributing a reload timeout to a specific
 * upload load) — do not use to gate control flow.
 */
export function getPendingOperationCount(): number {
  return pendingOperations.size;
}
